/**
 * The only place that calls Firebase callables. A successful write refreshes the
 * React Query cache, so no page shows data older than its own last save (T-239).
 */

import type { Query } from "@tanstack/react-query";
import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import { ADMIN_OPS } from "./adminOps";
import { callAdminProxy } from "./adminProxy";
import { functions } from "./firebase";
import { queryClient } from "./queryClient";

// A read is `get*`/`list*`/`preview*`/`check*` once an `admin`/`chef` namespace prefix is
// removed (adminGetUserDetail, chefListRecipes). Everything else is a write, so an
// unknown name over-invalidates and never leaves data stale.
export function isReadCallable(name: string): boolean {
  const verb = name.replace(/^(admin|chef)(?=[A-Z])/, "").replace(/^./, (c) => c.toLowerCase());
  return /^(get|list|preview|check)(?=[A-Z])/.test(verb);
}

// A query with `meta: { manualRefresh: true }` is expensive and refreshed by hand only.
export const refreshesOnWrite = (query: Query): boolean => query.meta?.manualRefresh !== true;

// Awaited by a write: when a save resolves, the screen already shows the saved data.
export const refreshAfterWrite = () => queryClient.invalidateQueries({ predicate: refreshesOnWrite });

export function callable<T = unknown, R = unknown>(name: string) {
  const sdkCall = httpsCallable<T, R>(functions, name);
  // Admin operations go to the adminProxy router (T-252).
  const fn = (data?: T): Promise<HttpsCallableResult<R>> =>
    ADMIN_OPS.has(name)
      ? (callAdminProxy<T, R>(name, data) as Promise<HttpsCallableResult<R>>)
      : sdkCall(data as T);
  return async (data?: T): Promise<HttpsCallableResult<R>> => {
    const result = await fn(data);
    if (!isReadCallable(name)) await refreshAfterWrite();
    return result;
  };
}
